<template>
  <div :class="{'timeline-view': true, 'no-filters': profile.role == 'Reporter'}">
    <button v-if="canCreate" class="btn sml creation createProblem" @click="createProblem">Report Problem</button>
    <header-view />
    <problem-filters v-if="profile.role !== 'Reporter'" :totals="totals" :filters="filters" :selectedFilters="selectedFilters" :sorts="sorts" @sortChanged="sortChanged" @filtersChanged="filtersChanged" />
    <div :class="{ 'loaded': dataLoaded, 'timeline': true }">
      <div v-if="!dataLoaded" class="loader">Loading...</div>
      <div v-if="problems.length === 0 && dataLoaded"><h3>No Problems</h3></div>
      <new-problem-view v-for="problem in problems" :canedit="profile.role === 'Admin'" v-bind:key="problem['.id']" :problem="problem" @imagePicked="imagePicked" @editProblem="editProblem" />
    </div>
    <div v-if="selectedImage" class="modal" @click="selectedImage = null">
      <div class="inner">
        <span class="close" @click="selectedImage = null">close</span>
        <img :src="selectedImage.downloadUrl" />
      </div>
    </div>
    <problem-editor-modal :create="create" :edit="edit" :profile="profile" :fulledit="['Admin','Reporter'].indexOf(profile.role) !== -1" :problem="problemToEdit" @cancel="problemEditorCanceled" @created="problemEditorCreated" @updated="problemEditorUpdated" />
  </div>
</template>

<script>
  import database from '@/api/database.js'
  import NewProblemView from '@/components/NewProblemView.vue'
  import HeaderView from '@/components/HeaderView.vue'
  import ProblemFilters from '@/components/ProblemFilters.vue'
  import ProblemEditorModal from '@/components/ProblemEditorModal.vue'
  import { compareAsc } from 'date-fns'
  import { uuid } from '@/utils'

  export default {
    name: 'Timeline',
    components: {
      HeaderView, ProblemFilters, NewProblemView, ProblemEditorModal
    },
    data () {
      return {
        problems: [],
        dataLoaded: false,
        totals: {
          problems: 0,
          rooms: 0,
          schools: 0,
          people: 0
        },
        filters: [],
        selectedFilters: {},
        doNotSortOrFilter: ['Floor','Role'],
        sorts: [
          {'label': 'Last Updated', 'key': 'lastUpdated'},
          {'label': 'Problem Reported Date', 'key': 'problemReportedDate'}
        ],
        sortKey: 'lastUpdated',
        selectedImage: undefined,
        problemToEdit: undefined,
        edit: false,
        create: false,
        profile: {
          role: 'tbd'
        }
      }
    },
    async mounted () {
      // register an event handler before subscribing to the updates
      database.EventBus.$on('problem-added-remotely', this.problemAddedRemotely)
      database.EventBus.$on('problem-modified-remotely', this.problemModifiedRemotely)
      database.EventBus.$on('problem-removed-remotely', this.problemRemovedRemotely)
      database.getCurrentUserAndProfile().then(userAndProfile => {
        this.profile = userAndProfile.profile
      })
      await database.getFields().then(fields => {
        Object.keys(fields).forEach(fieldName => {
          if (this.doNotSortOrFilter.indexOf(fields[fieldName].shortLabel) === -1) {
            this.filters.push(fields[fieldName])
            if(fieldName === 'status' && ['Admin','ViewAdmin'].indexOf(this.profile.role) > -1) { // important to only default to reported for admins and view admins ... sdp users will want to see all the problems by last updated
              console.log('setting default to status reported', fields[fieldName].values[0])
              this.selectedFilters['status'] = [fields['status'].values[0]]
            }
            // this.sorts.push({'label': fields[fieldName].shortLabel, 'key': fieldName})
            // i don't think this actually works
          }
        })
      })
      // Non admins do not have reported filter set by default
      if (['Admin','ViewAdmin'].indexOf(this.profile.role) == -1) {
        database.getProblems().then(problems => {
          this.dataLoaded = true
          this.problems = problems
          this.totals = database.calculateTotals(this.problems)
          this.cleanForTimelineView(this.problems, this.sortKey) 
        }).catch(NotFound => {
          console.log(NotFound)
          this.dataLoaded = true
        })
      }
    },
    computed: {
      canCreate () {
        if (['Admin'].indexOf(this.profile.role) > -1) {
          return true
        }
        if (['Reporter'].indexOf(this.profile.role) > -1) {
          return true
        }
        return false
      }
    },
    methods: {
      problemAddedRemotely (problem) {
        // console.log('➕ %s %s', problem.status.value, problem.status.lastModifiedDate)
        if (database.problemPassesFilters(this.selectedFilters, problem)) {
          if ((this.profile.role === 'Reporter' && problem.status.value > -1) || this.profile.role !== 'Reporter') {
            this.totals = database.calculateTotals(this.problems)
            this.problems.unshift(problem)
          }
        }
      },
      problemModifiedRemotely (problem) {
        if (this.problemToEdit && this.problemToEdit.id === problem.id) {
          this.problemToEdit = problem
        }
        // console.log('💋 role: %s %s %s', this.profile.role, problem.status.value, problem.status.lastModifiedDate, problem)
        if (problem.status.value === -1 && (this.profile.role === 'Reporter' || !(this.selectedFilters.status && this.selectedFilters.status.find(s => s.value === -1)))) {
          const index = this.problems.findIndex(p => p.id === problem.id)
          if (index > -1) {
            this.problems.splice(index,1)
            this.totals = database.calculateTotals(this.problems)
          }
        } else if (database.problemPassesFilters(this.selectedFilters, problem)) {
          const index = this.problems.findIndex(p => p.id === problem.id)
          if (index > -1) {
            this.problems.splice(index,1,problem)
          } else {
            this.problems.unshift(problem)
            this.cleanForTimelineView(this.problems, this.sortKey) 
          }
        } else {
          this.problems.splice(this.problems.findIndex(p => p.id === problem.id),1)
        }
        this.sortKey = 'lastUpdated'
        this.cleanForTimelineView(this.problems, this.sortKey) 
        this.totals = database.calculateTotals(this.problems)
      },
      problemRemovedRemotely (problemId) {
        // console.log('🗑 %s', problemId)
        const index = this.problems.findIndex(p => p.id === problemId)
        if (index > -1) {
          this.problems.splice(index,1)
          this.totals = database.calculateTotals(this.problems)
        }
      },
      imagePicked (image) {
        this.selectedImage = image
      },
      filtersChanged () {
        database.queryProblems(this.selectedFilters).then(problems => {
          if (!this.selectedFilters.status || (this.selectedFilters.status && this.selectedFilters.status.findIndex(s => s.value === '-1') === -1)) {
            problems = problems.filter(p => p.status.value > -1)
          }
          this.problems = problems
          this.dataLoaded = true
          this.cleanForTimelineView(this.problems, this.sortKey) 
          this.totals = database.calculateTotals(problems)
        }).catch(noSelectedFilters => {
          console.log('🗃', noSelectedFilters)
          this.problems = []
          this.dataLoaded = true
          this.totals = database.calculateTotals([])
        })
      },
      sortChanged (sortKey) {
        this.sortKey = sortKey
        this.cleanForTimelineView(this.problems, sortKey)
      },
      cleanForTimelineView (data, sortKey) {
        var sort = function (a,b) {
          const aLabel = a[sortKey].labels.short.toString()
          const bLabel = b[sortKey].labels.short.toString()
          return aLabel.localeCompare(bLabel)
        }
        if (sortKey === 'problemReportedDate') {
          sort = function (a,b) {
            let aTime = database.getProblemReportedTimeForSorting(a)
            let bTime = database.getProblemReportedTimeForSorting(b)
            return bTime - aTime
          }
        } 
        if (sortKey === 'lastUpdated') {
          sort = function (a,b) {
            try {
              const compare = compareAsc(b.status.lastModified, a.status.lastModified)
              return compare
            } catch (ignored) {}
            return 0
          }
        }
        this.problems = data.sort((a, b) => { return sort(a, b) })
        // console.log(data)
      },
      createProblem () {
        const prob = database.assembleProblem()
        prob.status.value = 0
        database.assembleLabels(prob).then(problem => {
          this.problemToEdit = problem
          this.edit = true
          this.create = true
          this.$modal.show('problem-container')
        })
      },
      editProblem (problem) {
        this.problemToEdit = JSON.parse(JSON.stringify(problem))
        this.$modal.show('problem-container')
      },
      problemEditorCanceled () {
        this.$modal.hide('problem-container')
        this.edit = false
        this.create = false
        this.problemToEdit = undefined
      },
      problemEditorCreated (createdProblem) {
        this.edit = false
        this.create = false
        this.problemEditorCanceled()
      },
      problemEditorUpdated (updatedProblem) {
        this.edit = false
        this.create = false
        this.problemEditorCanceled()
      }
    },
  }
</script>

<style scoped>
  .createProblem {
    float: right;
    margin-top: 15px;
    margin-right: 25px;
  }
  .timeline-view {
    padding-top: 55px;
  }
  .timeline-view.no-filters .createProblem {
    margin-top: -20px;
  }
  .timeline-view.no-filters {
    padding-top: 90px;
  }
  .timeline {
    margin: 13px 25px;
    display: grid;
    grid-row-gap: 13px;
  }
  @media only screen and (min-width: 680px) {    
    .timeline {
      grid-template-columns: 49% 50%;
      grid-template-rows: auto;
      grid-column-gap: 1%;
    }
  }
  @media only screen and (min-width: 1024px) {    
    .timeline {
      grid-template-columns: 24% 25% 24% 24%;
    }
  }
  .loaded .loader {
    opacity: 0;
  }
</style>